import * as Sentry from "@sentry/react";

/**
 * Mirror of `SENSITIVE_FIELD_NAMES` in
 * `artifacts/api-server/src/lib/logger.ts`. Keep these two lists in
 * sync — both packages can't share a runtime import (the SPA must not
 * pull in api-server code), so we duplicate the exact list and check
 * for drift in code review. Anything matching here is replaced with
 * "[redacted]" inside Sentry events before they leave the browser.
 */
const SENSITIVE_FIELD_NAMES: readonly string[] = [
  "password",
  "passwordHash",
  "currentPassword",
  "newPassword",
  "confirmPassword",
  "apiKey",
  "api_key",
  "secret",
  "token",
  "accessToken",
  "refreshToken",
  "sessionId",
  "session",
  "cookie",
  "set-cookie",
  "authorization",
  "x-whop-signature",
  "recoveryCode",
  "recoveryCodes",
  "codeHash",
  "totpSecret",
  "mfaSecret",
  "mfa_secret",
];
const SENSITIVE_KEY_SET = new Set(SENSITIVE_FIELD_NAMES.map((s) => s.toLowerCase()));

function scrub<T>(value: T, depth = 0): T {
  if (depth > 8 || value == null) return value;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1)) as unknown as T;
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY_SET.has(k.toLowerCase()) ? "[redacted]" : scrub(v, depth + 1);
    }
    return out as unknown as T;
  }
  return value;
}

let initialized = false;

/**
 * Initialize Sentry for the SPA. Safe no-op when `VITE_SENTRY_DSN` is
 * not set — Sentry is purely operator-side telemetry; the app must
 * boot in dev / preview / first-time deploys without it.
 */
export function initSentry(): boolean {
  if (initialized) return true;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      try {
        if (event.request) event.request = scrub(event.request);
        if (event.extra) event.extra = scrub(event.extra);
        if (event.contexts) event.contexts = scrub(event.contexts);
        if (event.tags) event.tags = scrub(event.tags);
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((b) => scrub(b));
        }
      } catch {
        return null;
      }
      return event;
    },
  });
  initialized = true;
  return true;
}

export { Sentry };
